import React from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Divider, Stack } from '@lyearn/ui';
import ErrorState, { ErrorBoundary } from '@/components/Error';
import { FilterOperators } from '@/components/Filter/types';
import SortMenu from '@/components/FilterManager/SortMenu';
import { SortMenuValue } from '@/components/FilterManager/SortMenu/types';
import BaseEntityFilter from '@/components/Filters/EntityFilter/BaseEntityFilter';
import FeaturedTagFilter from '@/components/Filters/FeaturedTagFilter';
import NestedFilter from '@/components/Filters/NestedFilter';
import { FilterOperatorEnum } from '@/types/schema';

import {
  FilterManager,
  FilterState,
  SortAndFilterItem,
  SortAndFilterProps,
  SortState,
} from '../../../types';
import DateRangeFilterContainer from '../DateRangeFilterContainer';
import SingleSelectFilterContainer from '../SingleSelectFilterContainer';
import StaticFilter from '../StaticFilter';
import { sortAndFilterContainerStyles } from './styles';

export const getSelectedOperatorTillBESupport = (t: TFunction): FilterOperators => ({
  id: FilterOperatorEnum.Is,
  label: t('Common:Filter.Is'),
  value: FilterOperatorEnum.Is,
});

type RenderSortAndFilterItemProps = {
  item: SortAndFilterItem;
  filter: FilterState;
  sort: SortState;
  setFilter: (filter: any) => void;
  setSort: (sort: any) => void;
  open?: boolean;
  onToggle?: (val: boolean) => void;
  onRemove?: () => void;
  children?: React.ReactNode;
};

export const RenderSortAndFilterItem = ({
  item,
  filter,
  sort,
  setFilter,
  setSort,
  open,
  onToggle,
  onRemove,
  children,
}: RenderSortAndFilterItemProps) => {
  const { t } = useTranslation('Group'); // todo make Operator.json

  if (!item.isEnabled) {
    return null;
  }

  if (item.type === FilterManager.Divider) {
    return <Divider flexItem className="my-4" orientation="vertical" />;
  }

  if (item.type === FilterManager.Sort) {
    return (
      <SortMenu
        key={item.type}
        defaultSortById={item.defaultSortById}
        items={item.items}
        sort={sort as SortMenuValue}
        onSortChange={setSort}
      />
    );
  }

  if (item.type === FilterManager.SingleSelectFilter) {
    return (
      <SingleSelectFilterContainer
        key={item.id}
        filter={filter}
        filterId={item.id}
        selectedOperator={item.hideOperator ? undefined : getSelectedOperatorTillBESupport(t)}
        setFilter={setFilter}
        {...item}
      />
    );
  }

  if (item.type === FilterManager.Filter) {
    return (
      <StaticFilter
        key={item.id}
        defaultPlaceholder={item.defaultPlaceholder}
        filter={filter}
        filterId={item.id}
        groups={item.groups}
        listVariant={item.listVariant}
        open={open}
        pillProps={{
          isNonEditable: item.isNonEditable,
        }}
        selectedOperator={getSelectedOperatorTillBESupport(t)}
        setFilter={setFilter}
        onToggle={onToggle}>
        {children}
      </StaticFilter>
    );
  }

  if (item.type === FilterManager.DateRange) {
    return (
      <DateRangeFilterContainer
        key={item.id}
        defaultPlaceholder={item.defaultPlaceholder}
        filter={filter}
        filterId={item.id}
        options={item.options}
        selectedOperator={getSelectedOperatorTillBESupport(t)}
        setFilter={setFilter}
      />
    );
  }

  if (item.type === FilterManager.Nested) {
    return <NestedFilter key={item.id} filter={filter} item={item} setFilter={setFilter} />;
  }

  if (item.type === FilterManager.Entity) {
    return (
      <BaseEntityFilter
        key={item.id}
        defaultPlaceholder={item.defaultPlaceholder}
        filter={filter}
        id={item.id}
        initialSelectedOperator={getSelectedOperatorTillBESupport(t)}
        listConfig={item.listConfig}
        pillProps={{
          onRemove: onRemove,
          isNonEditable: item.isNonEditable,
        }}
        queryParams={item.queryParams}
        setFilter={setFilter}
        uiConfig={item.uiConfig}
      />
    );
  }

  if (item.type === FilterManager.Custom) {
    return item.renderer({ filter, setFilter });
  }

  if (item.type === FilterManager.FeaturedTag) {
    return <FeaturedTagFilter key={item.id} filter={filter} item={item} setFilter={setFilter} />;
  }

  return null;
};

function SortAndFilter(props: SortAndFilterProps) {
  const { t } = useTranslation('Group'); // todo make Operator.json
  const {
    className,
    sortAndFilterItems,
    filter,
    sort,
    externalComponents = null,
    setFilter,
    setSort,
  } = props;

  return (
    <ErrorBoundary fallback={ErrorState}>
      <Stack
        className={cn(
          className,
          sortAndFilterContainerStyles,
          'w-full flex-wrap xs:max-w-full xs:flex-nowrap xs:overflow-x-auto',
        )}
        gap={8}>
        {sortAndFilterItems.map((item, index) => (
          <RenderSortAndFilterItem
            key={'id' in item ? item.id : index}
            filter={filter}
            item={item}
            setFilter={setFilter}
            setSort={setSort}
            sort={sort}
          />
        ))}
        {externalComponents}
      </Stack>
    </ErrorBoundary>
  );
}

export default SortAndFilter;
